import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, Edit, Shield } from 'lucide-react';
import AuthContext from '../../../../../context/AuthContext';
import { useToast } from '../../../../../context/ToastContext';
import historyService from '../../../../../services/historyService';

export default function ActivityLogsDetailes() {
  const { historyType, id } = useParams(); // Get both historyType and id from URL
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);
  const { showError } = useToast();
  
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivityLogDetail();
  }, [historyType, id]);

  const fetchActivityLogDetail = async () => {
    setLoading(true);
    try {
      const result = await historyService.getActivityHistoryDetail(historyType, id, authTokens.access);
      if (result.success) {
        setLog(result.data);
      } else {
        showError(`Erreur: ${result.error}`);
        setTimeout(() => navigate(-1), 2000);
      }
    } catch (error) {
      showError("Impossible de charger le détail de l'historique");
      setTimeout(() => navigate(-1), 2000);
    } finally {
      setLoading(false);
    }
  };

  const renderValue = (value) => {
    if (value === null || value === undefined) {
      return 'aucune valeur';
    }
    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return `[${value.map(item => renderValue(item)).join(', ')}]`;
      }
      if (value.risk_name) return value.risk_name;
      if (value.activity_name) return value.activity_name;
      if (value.process_name) return value.process_name;
      if (value.action) return `Action: ${value.action}`;
      if (value.email) return value.email;
      if (value.name) return value.name;
      return JSON.stringify(value);
    }
    return String(value);
  };

  const renderChanges = (changesSummary) => {
    if (!changesSummary || Object.keys(changesSummary).length === 0) {
      return <p className="text-sm text-gray-500">Aucun changement détaillé enregistré.</p>;
    }

    const changes = [];

    // Handle actions (created, updated, deleted) - for activity_risk
    if (changesSummary.actions) {
      const actions = changesSummary.actions;
      
      // Created actions
      if (actions.created && actions.created.length > 0) {
        actions.created.forEach((action, idx) => {
          changes.push(
            <li key={`created-${idx}`} className="text-sm">
              <strong className="font-semibold text-gray-800">Action créée:</strong>
              <div className="ml-4 mt-2 bg-green-50 p-3 rounded border border-green-200">
                <p className="text-green-800">
                  <strong>Description:</strong> {action.new?.action || 'N/A'}
                </p>
                {action.new?.dueDate && (
                  <p className="text-green-800 mt-1">
                    <strong>Date d'échéance:</strong> {new Date(action.new.dueDate).toLocaleDateString('fr-FR')}
                  </p>
                )}
                {action.new?.assignedUserId && (
                  <p className="text-green-800 mt-1">
                    <strong>Utilisateur assigné ID:</strong> {action.new.assignedUserId}
                  </p>
                )}
              </div>
            </li>
          );
        });
      }

      // Updated actions
      if (actions.updated && actions.updated.length > 0) {
        actions.updated.forEach((action, idx) => {
          if (action.changes && Object.keys(action.changes).length > 0) {
            changes.push(
              <li key={`updated-${idx}`} className="text-sm">
                <strong className="font-semibold text-gray-800">Action modifiée (ID: {action.id}):</strong>
                <ul className="ml-4 mt-2 space-y-2 bg-blue-50 p-3 rounded border border-blue-200">
                  {Object.entries(action.changes).map(([field, values]) => (
                    <li key={field} className="text-sm">
                      <strong className="text-gray-700">{field}:</strong>
                      <div className="ml-2 mt-1">
                        <span className="text-red-600 block">Ancien: {renderValue(values.old)}</span>
                        <span className="text-green-600 block">Nouveau: {renderValue(values.new)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </li>
            );
          }
        });
      }

      // Deleted actions
      if (actions.deleted && actions.deleted.length > 0) {
        actions.deleted.forEach((action, idx) => {
          changes.push(
            <li key={`deleted-${idx}`} className="text-sm">
              <strong className="font-semibold text-gray-800">Action supprimée (ID: {action.id}):</strong>
              <div className="ml-4 mt-2 bg-red-50 p-3 rounded border border-red-200">
                <p className="text-red-800">
                  <strong>Description:</strong> {action.old?.action || 'N/A'}
                </p>
                {action.old?.dueDate && (
                  <p className="text-red-800 mt-1">
                    <strong>Date d'échéance:</strong> {new Date(action.old.dueDate).toLocaleDateString('fr-FR')}
                  </p>
                )}
              </div>
            </li>
          );
        });
      }
    }

    // Handle consequence changes
    if (changesSummary.consequence) {
      const { old, new: newVal } = changesSummary.consequence;
      changes.push(
        <li key="consequence" className="text-sm">
          <strong className="font-semibold text-gray-800">Conséquence:</strong>
          <div className="ml-4 mt-1">
            <span className="text-red-600 block">Ancien: {renderValue(old)}</span>
            <span className="text-green-600 block">Nouveau: {renderValue(newVal)}</span>
          </div>
        </li>
      );
    }

    // Handle probability changes
    if (changesSummary.probability) {
      const { old, new: newVal } = changesSummary.probability;
      changes.push(
        <li key="probability" className="text-sm">
          <strong className="font-semibold text-gray-800">Probabilité:</strong>
          <div className="ml-4 mt-1">
            <span className="text-red-600 block">Ancien: {renderValue(old)}</span>
            <span className="text-green-600 block">Nouveau: {renderValue(newVal)}</span>
          </div>
        </li>
      );
    }

    // Handle risks changes (for activity type)
    if (changesSummary.risks) {
      const { from: oldRisks = [], to: newRisks = [] } = changesSummary.risks;
      changes.push(
        <li key="risks" className="text-sm">
          <strong className="font-semibold text-gray-800">Risques:</strong>
          <div className="ml-4 mt-2 space-y-3">
            <div>
              <span className="text-sm font-medium text-red-600">Anciens risques:</span>
              <div className="ml-2 mt-1 text-xs text-gray-600">
                {oldRisks.length === 0 ? (
                  <span className="italic">Aucun risque</span>
                ) : (
                  oldRisks.map((risk, index) => (
                    <div key={index} className="border-l-2 border-red-200 pl-2 mb-2 py-1">
                      <div><strong>Nom:</strong> {risk.risk_name}</div>
                      <div><strong>Description:</strong> {risk.description}</div>
                      <div><strong>Probabilité:</strong> {risk.probability}</div>
                      <div><strong>Statut:</strong> {risk.status}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div>
              <span className="text-sm font-medium text-green-600">Nouveaux risques:</span>
              <div className="ml-2 mt-1 text-xs text-gray-600">
                {newRisks.length === 0 ? (
                  <span className="italic">Aucun risque</span>
                ) : (
                  newRisks.map((risk, index) => (
                    <div key={index} className="border-l-2 border-green-200 pl-2 mb-2 py-1">
                      <div><strong>Nom:</strong> {risk.risk_name}</div>
                      <div><strong>Description:</strong> {risk.description}</div>
                      <div><strong>Probabilité:</strong> {risk.probability}</div>
                      <div><strong>Statut:</strong> {risk.status}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </li>
      );
    }

    // Handle any other direct field changes (from/to or old/new structure)
    Object.entries(changesSummary).forEach(([field, values]) => {
      // Skip already handled fields
      if (field === 'actions' || field === 'consequence' || field === 'probability' || field === 'risks') {
        return;
      }
      
      // Check if it has old/new or from/to structure
      if (values && typeof values === 'object') {
        const oldVal = values.old ?? values.from;
        const newVal = values.new ?? values.to;
        
        if (oldVal !== undefined || newVal !== undefined) {
          changes.push(
            <li key={field} className="text-sm">
              <strong className="font-semibold text-gray-800">{field}:</strong>
              <div className="ml-4 mt-1">
                <span className="text-red-600 block">Ancien: {renderValue(oldVal)}</span>
                <span className="text-green-600 block">Nouveau: {renderValue(newVal)}</span>
              </div>
            </li>
          );
        }
      }
    });

    if (changes.length === 0) {
      return <p className="text-sm text-gray-500">Aucun changement détaillé enregistré.</p>;
    }

    return <ul className="space-y-3">{changes}</ul>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!log) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Détail non trouvé.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Retour
        </button>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Détail de l'Historique</h1>
              <div className="flex items-center space-x-2 mt-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  log.history_type === 'activity' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
                }`}>
                  {log.history_type === 'activity_risk' && <Shield className="h-3 w-3 mr-1" />}
                  {log.history_type === 'activity' ? 'Activité' : 'Risque d\'Activité'}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  log.action_type === 'create' ? 'bg-green-100 text-green-800' :
                  log.action_type === 'update' ? 'bg-blue-100 text-blue-800' :
                  log.action_type === 'delete' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {log.action_type === 'create' ? 'Création' :
                   log.action_type === 'update' ? 'Modification' :
                   log.action_type === 'delete' ? 'Suppression' :
                   log.action_type}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-600">
            <div className="flex items-center">
              <User className="h-4 w-4 mr-2 text-gray-400" />
              <span><strong>Fait par:</strong> {log.done_by_email}</span>
            </div>
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-2 text-gray-400" />
              <span><strong>Date:</strong> {new Date(log.action_date).toLocaleDateString('fr-FR')}</span>
            </div>
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-2 text-gray-400" />
              <span><strong>Heure:</strong> {new Date(log.action_date).toLocaleTimeString('fr-FR')}</span>
            </div>
            <div className="flex items-center">
              <Edit className="h-4 w-4 mr-2 text-gray-400" />
              <span><strong>Nom de l'activité:</strong> {log.activity_name}</span>
            </div>
            {log.risk_name && (
              <div className="flex items-center md:col-span-2">
                <Shield className="h-4 w-4 mr-2 text-gray-400" />
                <span><strong>Nom du risque:</strong> {log.risk_name}</span>
              </div>
            )}
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Description</h3>
            <p className="text-gray-700">{log.description}</p>
          </div>

          {log.changes_summary && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Changements Détaillés</h3>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                {renderChanges(log.changes_summary)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}




